const PORTAL_ID = "debug-capture-root";

export const DEBUG_CAPTURE_UI_SELECTOR =
  "[data-debug-capture-root], [data-debug-capture-trigger], [data-debug-capture-choice], [data-debug-capture-permission], [data-debug-capture-recording], [data-debug-capture-handoff], [data-debug-capture-processing], [data-debug-capture-resume]";

function applyPortalRootStyles(root: HTMLElement): void {
  root.setAttribute("data-debug-capture-root", "true");
  root.style.setProperty("position", "fixed", "important");
  root.style.setProperty("left", "0", "important");
  root.style.setProperty("bottom", "0", "important");
  root.style.setProperty("width", "0", "important");
  root.style.setProperty("height", "0", "important");
  root.style.setProperty("overflow", "visible", "important");
  root.style.setProperty("pointer-events", "none", "important");
  root.style.setProperty("z-index", "2147483647", "important");
  root.style.removeProperty("inset");
  root.style.removeProperty("isolation");
}

/** Small anchor for the floating button — not a full-screen layer. */
export function getDebugCapturePortalRoot(): HTMLElement {
  if (typeof document === "undefined") {
    throw new Error("Debug capture portal requires document");
  }

  let root = document.getElementById(PORTAL_ID);

  if (!root) {
    root = document.createElement("div");
    root.id = PORTAL_ID;
    document.body.appendChild(root);
  }

  applyPortalRootStyles(root);
  return root;
}

export function getDebugCaptureDialogPortal(): HTMLElement {
  if (typeof document === "undefined") {
    throw new Error("Debug capture dialog portal requires document");
  }

  return document.body;
}

export function enableDebugCapturePointerEvents(element: HTMLElement): void {
  element.style.setProperty("pointer-events", "auto", "important");
}

export function clearInertFromDebugCapture(): void {
  document.querySelectorAll<HTMLElement>(DEBUG_CAPTURE_UI_SELECTOR).forEach(
    (element) => {
      element.inert = false;
      element.removeAttribute("inert");
      element.removeAttribute("aria-hidden");
    }
  );
}

export function isDebugCaptureUiElement(element: EventTarget | null): boolean {
  if (!(element instanceof Element)) {
    return false;
  }

  return Boolean(element.closest(DEBUG_CAPTURE_UI_SELECTOR));
}

/** Form fields that should keep working while the picker is armed (Alt+click to capture). */
const FORM_CONTROL_SELECTOR = [
  'input:not([type="hidden"]):not([disabled])',
  "textarea:not([disabled])",
  "select:not([disabled])",
  '[contenteditable=""]',
  '[contenteditable="true"]',
  '[contenteditable="plaintext-only"]',
  '[role="textbox"]',
  '[role="combobox"]',
  '[role="searchbox"]',
  '[role="spinbutton"]',
  '[role="slider"]',
  '[role="switch"]',
  '[role="checkbox"]',
  '[role="radio"]',
  "label[for]",
].join(", ");

export function isFormControlElement(element: HTMLElement): boolean {
  if (
    element.matches(FORM_CONTROL_SELECTOR) ||
    element.closest(FORM_CONTROL_SELECTOR)
  ) {
    return true;
  }

  const label = element.closest("label");
  return Boolean(label?.querySelector("input, textarea, select"));
}

/** When armed, normal clicks on form fields pass through; Alt+click captures them. Buttons and links capture on normal click. */
export function shouldInterceptPickClick(
  event: MouseEvent,
  target: HTMLElement | null
): target is HTMLElement {
  if (!target) {
    return false;
  }

  if (isFormControlElement(target) && !event.altKey) {
    return false;
  }

  return true;
}

function normalizePickTarget(element: HTMLElement): HTMLElement {
  const cell = element.closest("td, th");
  if (cell instanceof HTMLElement) {
    return cell;
  }

  return element;
}

export function getPickTargetAtPoint(x: number, y: number): HTMLElement | null {
  const stack = document.elementsFromPoint(x, y);

  for (const element of stack) {
    if (!(element instanceof HTMLElement)) {
      continue;
    }

    if (isDebugCaptureUiElement(element)) {
      continue;
    }

    return normalizePickTarget(element);
  }

  return null;
}
